import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/performance-methodology")({
  head: () => ({
    meta: [
      { title: "Performance Methodology — ForexMind AI" },
      {
        name: "description",
        content:
          "How ForexMind AI calculates historical accuracy, R:R, and outcomes for published market intelligence.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "ForexMind AI" },
      { property: "og:title", content: "Performance Methodology — ForexMind AI" },
      {
        property: "og:description",
        content:
          "Definitions and rules used for the public ForexMind AI performance dashboard: resolution, R:R, exclusions, data freshness.",
      },
      { property: "og:url", content: "https://forexmind.ai/performance-methodology" },
      { property: "og:image", content: "https://forexmind.ai/og-image.png" },
      { property: "og:image:secure_url", content: "https://forexmind.ai/og-image.png" },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Performance Methodology — ForexMind AI" },
      {
        name: "twitter:description",
        content:
          "Definitions and rules used for the public ForexMind AI performance dashboard: resolution, R:R, exclusions, data freshness.",
      },
      { name: "twitter:image", content: "https://forexmind.ai/og-image.png" },
    ],
    links: [{ rel: "canonical", href: "https://forexmind.ai/performance-methodology" }],
  }),
  component: MethodologyPage,
});

function MethodologyPage() {
  return (
    <main className="mx-auto max-w-3xl px-6 py-12 text-foreground">
      <Button asChild variant="ghost" size="sm" className="text-muted-foreground">
        <Link to="/performance">
          <ArrowLeft className="mr-1.5 h-3 w-3" /> Back to performance
        </Link>
      </Button>

      <h1 className="mt-6 font-display text-3xl font-bold">Performance Methodology</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        How the figures shown on the public performance dashboard are calculated.
      </p>

      <div className="mt-8 space-y-6 text-sm leading-relaxed text-muted-foreground">
        <Section title="What counts as a market intelligence idea">
          Every directional view (BUY or SELL) published by the AI council is counted as a
          single market intelligence idea. WAIT / neutral views are tracked separately and
          excluded from accuracy calculations.
        </Section>
        <Section title="What counts as resolved">
          An idea is resolved when one of the following occurs: (a) the published take-profit
          level is reached first (recorded as a win), (b) the published stop-loss level is
          reached first (recorded as a loss), or (c) the idea expires without either level
          being touched (recorded as expired). Only resolved ideas feed the accuracy and R:R
          calculations.
        </Section>
        <Section title="How BUY/SELL ideas are measured">
          Outcomes are evaluated against the entry, stop-loss, and take-profit levels published
          at the time of the idea. Subsequent edits, additions, or scaling are not modelled.
        </Section>
        <Section title="How WAIT / neutral views are treated">
          WAIT views are excluded from the historical accuracy percentage because they do not
          have a directional outcome. They are still counted in the total ideas figure for
          completeness.
        </Section>
        <Section title="How expired ideas are treated">
          Expired ideas (no SL or TP reached within the published validity window) are reported
          separately and excluded from the historical accuracy calculation.
        </Section>
        <Section title="How R:R is calculated">
          The risk-to-reward ratio is taken from the published stop-loss and take-profit at the
          time of the idea, expressed as 1:N where N = |entry − take-profit| ÷ |entry − stop-loss|.
          The dashboard shows the simple mean of R:R across published ideas.
        </Section>
        <Section title="Spread / slippage / fees">
          Reported outcomes are level-to-level and do not include broker spread, slippage,
          commission, or swap. Real-world results may differ.
        </Section>
        <Section title="Data freshness">
          Aggregates are refreshed periodically (and on demand from the admin panel). The page
          shows the last refresh timestamp. Underlying price data is sourced from third-party
          providers and may be delayed or temporarily incorrect.
        </Section>
        <Section title="Timezone">
          All timestamps shown on the public dashboard are rendered in the visitor's local
          timezone. Outcome resolution uses UTC server time.
        </Section>
        <Section title="Why we publish this">
          We publish aggregate outcomes for transparency. Losing ideas are included on the
          same basis as winning ideas. Historical market intelligence is provided for
          transparency only and should not be interpreted as investment advice or as a
          forecast of future results.
        </Section>
        <Section title="Signal immutability and audit trail">
          Signal records cannot be deleted once resolved. All edits are logged with
          timestamp and reason in our compliance audit trail.{" "}
          <code className="rounded bg-muted px-1 py-0.5 text-xs">generated_at</code>{" "}
          records when the AI council produced the idea.{" "}
          <code className="rounded bg-muted px-1 py-0.5 text-xs">published_at</code>{" "}
          records when it became visible to users.
        </Section>
      </div>

      <nav className="mt-10 flex flex-wrap gap-4 border-t border-border/60 pt-6 text-xs text-muted-foreground">
        <Link to="/risk-disclosure" className="hover:text-foreground">Risk Disclosure</Link>
        <Link to="/terms" className="hover:text-foreground">Terms</Link>
        <Link to="/privacy" className="hover:text-foreground">Privacy Policy</Link>
        <Link to="/performance-methodology" className="hover:text-foreground">Performance Methodology</Link>
        <Link to="/performance" className="hover:text-foreground">Performance</Link>
        <a href="mailto:support@forexmind.ai" className="hover:text-foreground">Contact</a>
      </nav>
    </main>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      <p className="mt-1">{children}</p>
    </section>
  );
}
